/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * OrganizerRouteBuilderPage — /organizer/routes
 * ---------------------------------------------
 * A standalone course planner: draw a route, or load a GPX to adjust, and
 * download it as a GPX file. It needs no event, so an organizer can plan and
 * share a course (with a venue, the police, a timing partner) before the event
 * exists — and later upload the same file in the event form.
 *
 * Nothing is saved to the server. The course in progress is kept in this
 * browser's localStorage so a refresh or an accidental navigation does not lose
 * an hour of clicking; the GPX download is the deliverable.
 */

import React, { useEffect, useRef, useState } from 'react';
import RouteDrawer from '../../components/organizer/routes/RouteDrawer';
import OrgInput from '../../components/organizer/events/OrgInput';
import readGpxFile from '../../components/athlohive/gpx/readGpxFile';
import { downloadGpx, gpxFileName } from '../../components/athlohive/gpx/buildGpx';
import '../../styles/organizer.css';

const DRAFT_KEY = 'organizer.routeBuilder.draft';
const DEFAULT_NAME = 'My course';

function readDraft() {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || !Array.isArray(draft.points)) return null;
    const points = draft.points.filter((p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lng));
    return { name: typeof draft.name === 'string' ? draft.name : DEFAULT_NAME, points };
  } catch (e) {
    return null;
  }
}

function writeDraft(name, points) {
  try {
    if (!points.length) window.localStorage.removeItem(DRAFT_KEY);
    else window.localStorage.setItem(DRAFT_KEY, JSON.stringify({ name, points }));
  } catch (e) {
    // Private window or storage blocked: the page still works, it just forgets.
  }
}

export default function OrganizerRouteBuilderPage() {
  const [initial] = useState(readDraft);
  const [name, setName] = useState(initial?.name || DEFAULT_NAME);
  const [points, setPoints] = useState(initial?.points || []);
  // Bumped whenever a whole new course arrives, so the map re-frames and the
  // undo history starts over (see RouteDrawer).
  const [fitKey, setFitKey] = useState(0);
  const [status, setStatus] = useState(null); // { tone: 'ok' | 'err', text }
  const fileRef = useRef(null);

  useEffect(() => { writeDraft(name, points); }, [name, points]);

  const onPickFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    // Reset so choosing the same file again still fires a change.
    event.target.value = '';
    if (!file) return;
    const result = await readGpxFile(file);
    if (!result) {
      setStatus({
        tone: 'err',
        text: `${file.name} has no track, route or waypoints to read. Export it again as GPX and retry.`,
      });
      return;
    }
    setPoints(result.points);
    setName(file.name.replace(/\.gpx$/i, '').replace(/[-_]+/g, ' ').trim() || DEFAULT_NAME);
    setFitKey((key) => key + 1);
    setStatus({
      tone: 'ok',
      text: result.originalCount > result.points.length
        ? `Loaded ${file.name}: ${result.originalCount.toLocaleString()} recorded points, simplified to ${result.points.length} so you can edit them.`
        : `Loaded ${file.name}: ${result.points.length} points.`,
    });
  };

  const onDownload = () => {
    if (points.length < 2) return;
    downloadGpx(points, name || DEFAULT_NAME);
    setStatus({ tone: 'ok', text: `Downloaded ${gpxFileName(name || DEFAULT_NAME)}.` });
  };

  const onStartOver = () => {
    setPoints([]);
    setName(DEFAULT_NAME);
    setFitKey((key) => key + 1);
    setStatus(null);
  };

  return (
    <>
      <header className="org-topbar">
        <div>
          <h1>Route builder</h1>
          <p className="org-sub">Draw a course and download it as a GPX file for watches and bike computers</p>
        </div>
      </header>

      <div className="org-content">
        <div className="orgr-layout">
          <section className="org-card orgr-mapcard" aria-label="Course">
            <RouteDrawer points={points} onChange={setPoints} fitKey={fitKey} height={560} />
          </section>

          <aside className="orgr-side">
            <div className="org-card orgr-panel">
              <OrgInput
                label="Course name"
                value={name}
                onChange={setName}
                placeholder="Cubbon Park 5K loop"
                help={`Saved as ${gpxFileName(name || DEFAULT_NAME)}`}
              />
              <button
                type="button"
                className="org-btn"
                onClick={onDownload}
                disabled={points.length < 2}
              >
                <i className="bi bi-download" aria-hidden="true" /> Download GPX
              </button>
              {points.length < 2 && (
                <span className="orgf-help">Add at least two points to download the course.</span>
              )}
              <button type="button" className="org-btn ghost" onClick={() => fileRef.current?.click()}>
                <i className="bi bi-upload" aria-hidden="true" /> Load a GPX file
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".gpx,application/gpx+xml"
                className="orgr-fileinput"
                onChange={onPickFile}
                data-testid="route-builder-file"
                tabIndex={-1}
                aria-hidden="true"
              />
              {status && (
                <p className={`orgr-status${status.tone === 'err' ? ' err' : ''}`} role="status">
                  {status.text}
                </p>
              )}
            </div>

            <div className="org-card orgr-panel">
              <h2 className="orgr-h2">Using the file</h2>
              <p className="orgr-note">
                Import the GPX into Garmin Connect, Strava, Coros or Komoot as a course, or send
                it to your timing partner. To put it on an event, open the event&apos;s activity
                and upload it under <b>Course</b>, or draw it there directly.
              </p>
              <p className="orgr-note">
                Your course is kept in this browser until you start over. It is not saved to
                your account.
              </p>
              <button
                type="button"
                className="orgf-remove"
                onClick={onStartOver}
                disabled={!points.length}
              >
                Start over
              </button>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
